import { AlertTriangle, Clock, Ban, PlaneTakeoff, PlaneLanding, Route } from "lucide-react";

/**
 * LiveStatusPill — renders a canonical FlightStatus onto a card row.
 * Follows brief §4.2: nothing renders if data is missing; the pill NEVER presents
 * gate/terminal/belt as authoritative — freshness + a confirm-at-airport hint always accompany it.
 */

const STATUS_LABEL = {
  scheduled: "On time",
  delayed: "Delayed",
  boarding: "Boarding",
  departed: "Departed",
  en_route: "En route",
  landed: "Landed",
  arrived: "Arrived",
  cancelled: "Cancelled",
  diverted: "Diverted",
  unknown: null,
};

const STATUS_STYLE = {
  scheduled: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  delayed: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  boarding: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  departed: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  en_route: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  landed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  arrived: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  cancelled: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  diverted: "border-rose-500/40 bg-rose-500/10 text-rose-300",
};

const STATUS_ICON = {
  scheduled: PlaneTakeoff, delayed: Clock, boarding: PlaneTakeoff, departed: PlaneTakeoff,
  en_route: Route, landed: PlaneLanding, arrived: PlaneLanding, cancelled: Ban, diverted: AlertTriangle,
};

function freshness(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const min = Math.round((Date.now() - t) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function LiveStatusPill({ live, tone = "dark", compact = false }) {
  if (!live || !live.status || live.status === "unknown") return null;
  const label = STATUS_LABEL[live.status];
  if (!label) return null;
  const Icon = STATUS_ICON[live.status] || PlaneTakeoff;
  const cls = STATUS_STYLE[live.status] || "border-sand-500/40 bg-sand-500/10 text-sand-300";
  const delay = live.status === "delayed" && live.delay_minutes ? ` · ${live.delay_minutes}m` : "";
  const size = compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-1";
  return (
    <span
      data-testid="live-status-pill"
      className={`inline-flex items-center gap-1 font-mono uppercase tracking-wide border rounded-full ${cls} ${size}`}
    >
      <Icon size={compact ? 9 : 10} />
      {label}{delay}
    </span>
  );
}

/**
 * LiveGateRow — the freshness-aware gate/terminal/belt strip.
 * Every field renders only when present. Freshness is REQUIRED — never show a bare gate.
 */
export function LiveGateRow({ live, tone = "dark", showConfirmHint = true }) {
  if (!live) return null;
  const fields = [
    { key: "GATE", value: live.departure_gate },
    { key: "TERMINAL", value: live.departure_terminal },
    { key: "CHECK-IN", value: live.check_in_desk },
    { key: "ARR TERMINAL", value: live.arrival_terminal },
    { key: "BELT", value: live.baggage_belt },
  ].filter((f) => f.value);
  if (fields.length === 0 && live.status !== "cancelled" && live.status !== "diverted") return null;

  const fresh = freshness(live.fetched_at);
  const textCls = tone === "dark" ? "text-sand-300" : "text-sand-700";
  const keyCls = tone === "dark" ? "text-sand-500" : "text-sand-500";
  const valCls = tone === "dark" ? "text-white" : "text-ink";

  return (
    <div className="mt-2 flex flex-col gap-1" data-testid="live-gate-row">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {fields.map((f) => (
          <span key={f.key} className={`font-mono text-[11px] ${textCls}`}>
            <span className={keyCls}>{f.key} · </span>
            <span className={`${valCls} font-semibold`}>{f.value}</span>
          </span>
        ))}
        {live.diversion_airport_iata && (
          <span className="font-mono text-[11px] text-rose-300">
            <span className={keyCls}>DIVERTED → </span>
            <span className="text-rose-200 font-semibold">{live.diversion_airport_iata}</span>
          </span>
        )}
        {fresh && (
          <span className="font-mono text-[10px] text-sand-500" data-testid="live-freshness">
            as of {fresh}
          </span>
        )}
      </div>
      {showConfirmHint && fields.length > 0 && (
        <p className={`font-mono text-[10px] uppercase tracking-wide ${tone === "dark" ? "text-sand-500" : "text-sand-500"}`}>
          ✓ Confirm at the airport board — gates change.
        </p>
      )}
    </div>
  );
}
